import { useCallback, useRef } from 'react';
import { useToast } from '@/components/ui/use-toast';

const nsfwWords = [
  // English
  'nude','sex','porn','xxx','fuck','shit','bitch','slut','asshole','dick','cock','pussy','rape','kill','murder','hate','violence','nsfw','cum','boob','breast','penis','vagina','anal','blowjob','abuse','incest','pedo','genocide','suicide','selfharm','torture','gore','blood','death','dead','hang','lynch','shoot','stab','terror','bomb','racist','fag','nigger',
  // Most common Hindi
  'chutiya','chut','gaand','bhosdi','bhosdike','madarchod','behenchod','bhenchod','loda','lund','randi','gandu','gand','chodu','launda','laundiya','chutmar','kamina','harami','kutte','kutti','saala','saali','bakchod','jhant','jhantu','tatti'
];

// Create a Set for O(1) lookup performance
const nsfwWordsSet = new Set(nsfwWords);

export function containsNSFW(text: string): boolean {
  if (!text || text.length < 3) return false;
  const lower = text.toLowerCase();
  
  // Quick character check
  const suspiciousChars = /[sxfckgh]/i;
  if (!suspiciousChars.test(lower)) return false;
  
  // Use Set for faster lookup
  const sortedWords = Array.from(nsfwWordsSet).sort((a, b) => a.length - b.length);
  for (const word of sortedWords) {
    if (lower.includes(word)) return true;
  }
  return false;
}

/**
 * Hook for debounced NSFW validation
 * Returns true if NSFW content is detected (should block the update)
 */
export function useDebouncedNSFWCheck() {
  const { toast } = useToast();
  const lastCheckedValue = useRef<string>('');

  const checkNSFW = useCallback((value: string, fieldName: string) => {
    // If value hasn't changed, skip check
    if (value === lastCheckedValue.current) {
      return false;
    }

    // Immediate check for blocking (don't allow the text)
    const hasNSFW = containsNSFW(value);
    
    if (hasNSFW) {
      lastCheckedValue.current = value;
      toast({
        variant: "destructive",
        title: "NSFW Content Detected",
        description: "This field contains inappropriate words. Please use appropriate language.",
      });
      return true; // Block the update
    }

    // Update last checked value
    lastCheckedValue.current = value;
    return false; // Allow the update
  }, [toast]);

  return { checkNSFW };
}

